import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'
import type { TrackId } from 'types/track'
import type { Pro, Publisher } from 'types/writer'
import {
  getProDropdownOptions,
  getPros,
  getPublisherDropdownOptions,
  getPublishers,
  makeGetWriterNames,
  makeGetWritersAndArtists,
} from '@disco/state-management/selectors/contacts'
import HelpTooltip from 'components/common/tooltip'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'
import type { DropdownOption } from 'types/ui'

const writerPromptTextCreator = label => `Create new writer "${label}"`

const proPromptTextCreator = label => `Create new PRO "${label}"`

const publisherPromptTextCreator = label => `Create new publisher "${label}"`

function makeMapStateToProps() {
  const getWritersAndArtists = makeGetWritersAndArtists()
  const getWriterNames = makeGetWriterNames()
  return (state, { split, exclusionList, listOfWritersToCreate }) => {
    const listWriterIdToCreate = listOfWritersToCreate
      .map(item => {
        if (item.props.split.id !== split.id) {
          return item.getInputs().writer
        }

        return null
      })
      .filter(Boolean)
    const inclusionList = split && split.writer ? [split.writer.id] : []
    return {
      writers: getWritersAndArtists(state, {
        inclusionList,
      }),
      pros: getPros(state),
      publishers: getPublishers(state),
      invalidWriterOptions: getWriterNames(state, {
        ids: [...exclusionList, ...listWriterIdToCreate],
      }),
      proDropdownOptions: getProDropdownOptions(state),
      publisherDropdownOptions: getPublisherDropdownOptions(state),
      allExclusionList: [...exclusionList, ...listWriterIdToCreate],
    }
  }
}

const getStateFromProps = ({ split }: Props) => ({
  writer:
    split && split.writer
      ? {
          value: split.writer.id,
          label: split.writer.name,
        }
      : null,
  split: get(split, 'split', ''),
  publisher:
    split && split.publisher ? split.publisher.id.toString() : undefined,
  pro: split && split.pro ? split.pro.id.toString() : undefined,
})

type Props = {
  onSubmit: (...args: Array<any>) => any
  onCancel: (...args: Array<any>) => any
  track?: TrackId
  split: Record<string, any>
  type: 'add' | 'edit'
  serverError: string | null | undefined
  pros: Pro[]
  publishers: Publisher[]
  proDropdownOptions: DropdownOption[]
  publisherDropdownOptions: DropdownOption[]
  validate: (...args: Array<any>) => any
  getValidationMessages: (...args: Array<any>) => any
  exclusionList: number[]
  invalidWriterOptions: string[]
  getWriters: (...args: Array<any>) => any
  saveAction?: boolean
  inlineValidations?: boolean
  isFirstWriter: boolean
  editingKey: number
  onUnsavedChanges: (...args: Array<any>) => any
  listOfWritersToCreate: Record<string, any>[]
  allExclusionList: string[]
  defaultWriters: Record<string, any>[]
}
type OwnState = {
  pro: string | null | undefined
  publisher: string | null | undefined
  writer: Record<string, any> | null | undefined
  split: number | null | undefined
  updating: boolean
  splitString: string | null | undefined
}

class EditWriter extends Component<Props, OwnState> {
  validatorTypes = strategy.createSchema(
    {
      split: 'between:0,100',
      writer: 'required',
    },
    {
      'required.writer': 'Please select the writer',
      'required.split': 'Please enter writer split',
      'between.split': 'Split must be between 0 and 100%',
    },
  )
  state = { ...getStateFromProps(this.props), updating: false, splitString: '' }

  componentDidMount() {
    this.props.getWriters(this)
  }

  componentWillUnmount() {
    const id = this.props.split.id
    this.props.getWriters(this, id)
  }

  getOptionsFromResponse = results => {
    const options = results?.map(writer => ({
      label: writer.name,
      value: writer.id.toString(),
      data: writer,
    }))
    const filteredOptions = options.filter(
      ({ value }) => !this.props.allExclusionList.includes(value),
    )
    return filteredOptions
  }
  loadWriters = debounce((input, callback) => {
    if (!input && Boolean(this.props.defaultWriters.length)) {
      const options = this.getOptionsFromResponse(this.props.defaultWriters)
      return callback(null, {
        options: options,
      })
    }

    ;(async () => {
      try {
        const { results } = await api.getWriters(input, 20)
        const options = this.getOptionsFromResponse(results)
        return callback(null, {
          options: options,
        })
      } catch {
        return callback(null, {
          options: [],
        })
      }
    })()
  }, 300)
  getValidatorData = () => ({
    split: this.state.split,
    writer: this.state.writer?.value,
    publisher: this.state.publisher,
    pro: this.state.pro,
  })

  getChangesWriter(isValid) {
    const changeWriter =
      (this.state.writer?.value || '') !==
      String(this.props.split.writer?.id || '')
    const changeSplit =
      (this.state.split || 0) !== Number(this.props.split.split || 0)
    const changePro =
      (this.state.pro || '') !== String(this.props.split.pro?.id || '')
    const changePublisher =
      (this.state.publisher || '') !==
      String(this.props.split.publisher?.id || '')
    const hasUnsavedChanges =
      changeWriter || changeSplit || changePro || changePublisher
    this.props.onUnsavedChanges({
      id: this.props.editingKey,
      hasChanges: hasUnsavedChanges,
      isValid,
    })
  }

  _onSplitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isValidFormat = e.target.value.match(/^\d+\.?(\d?)+$/)

    if (isValidFormat || !e.target.value) {
      this.setState({
        split: Number(e.target.value),
        splitString: e.target.value,
      })
    }
  }
  _onSplitBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState(
      {
        split: Number(e.target.value),
        splitString: e.target.value,
      },
      this.validate,
    )
  }
  _onWriterChange = e => {
    const writer = e?.data
    this.setState(
      {
        writer: {
          label: writer ? writer.name : e?.value,
          value: writer ? writer.id : e?.value,
        },
        publisher:
          writer && get(writer, 'default_publisher', null)
            ? writer.default_publisher.toString()
            : undefined,
        pro:
          writer && get(writer, 'default_pro', null)
            ? writer.default_pro.toString()
            : undefined,
      },
      this.validate,
    )
  }
  _onProChange = pro => {
    this.setState(
      {
        pro: (pro && pro.value) || null,
      },
      this.validate,
    )
  }
  _onPublisherChange = publisher => {
    this.setState(
      {
        publisher: (publisher && publisher.value) || null,
      },
      this.validate,
    )
  }

  _formatterWriter({ writer, publisher, pro, track_ids, ...rest }) {
    const writerKey: string = typeof writer === 'string' ? 'name' : 'id'
    const resultPublisher = publisher ? publisher.name : this.state.publisher
    const resultPro = pro ? pro.name : this.state.pro
    return {
      writer: {
        [writerKey]: writer,
      },
      publisher:
        (resultPublisher && {
          name: resultPublisher,
        }) ||
        null,
      pro:
        (resultPro && {
          name: resultPro,
        }) ||
        null,
      track_ids,
      ...rest,
    }
  }

  onSubmit = async () => {
    let writer = this.getInputs()

    try {
      await new Promise<void>((resolve, reject) => {
        this.props.validate(error => {
          if (error) {
            reject(new Error('Invalid writer split'))
          } else {
            resolve()
          }
        })
      })
      this.setState({
        updating: true,
      })
      await this.props.onSubmit(this._formatterWriter(writer))

      // reset component state after adding new writer
      if (this.props.type === 'add') {
        // we don't need to set state because after update this component
        // will be hidden
        this.setState({
          updating: false,
        })
        this.setState({
          pro: null,
          writer: null,
          split: null,
          publisher: null,
        })
      }
    } catch (error) {
      this.setState({
        updating: false,
      })
      throw error
    }
  }

  getInputs() {
    const existingPublisherValue = this.props.publishers.find(
      p => p.id.toString() === this.state.publisher,
    )
    const existingProValue = this.props.pros.find(
      p => p.id.toString() === this.state.pro,
    )
    return {
      writer: this.state.writer?.value,
      split:
        this.state.split && parseFloat(this.state.split) > 0
          ? Number(this.state.split)
          : null,
      publisher: existingPublisherValue,
      pro: existingProValue,
      id: this.props.split ? this.props.split.id : undefined,
      track_ids: [this.props.track ? this.props.track : undefined],
    }
  }

  validate = async () => {
    if (this.props.inlineValidations) {
      let isValid = true
      await new Promise<void>(res => {
        this.props.validate(error => {
          isValid = !error
          res()
          if (!error)
            this.setState({
              updating: true,
            })
        })
      })
      this.getChangesWriter(isValid)
      return isValid
    }
  }
  onCancel = () => {
    const id = this.props.split.id
    this.props.onCancel(id)
    this.props.getWriters(this, id)
  }
  _trackContactsClick = () =>
    mixpanel.sendPureEvent('Open contacts via link in Writers split tab')

  // static getDerivedStateFromProps(props, state) {
  //   const difference = props.writerDropdownOptions.filter(({ label }) => {
  //     return !state.writerDropdownOptions.find(o => o.label === label)
  //   })
  //   const writerDropdownOptions = [
  //     ...state.writerDropdownOptions,
  //     ...difference,
  //   ]
  //   let result = null
  //   if (!isEqual(writerDropdownOptions, state.writerDropdownOptions)) {
  //     result = { writerDropdownOptions }
  //   }
  //   return null
  // }
  renderErrorMessage(error) {
    return (
      <div className="TextField_Error">
        <svg className="TextField_Error_Icon" width="16" height="16">
          <path
            fill="currentcolor"
            d="M8 1.333A6.67 6.67 0 0 0 1.333 8 6.67 6.67 0 0 0 8 14.667 6.67 6.67 0 0 0 14.667 8 6.67 6.67 0 0 0 8 1.333zm0 7.334A.669.669 0 0 1 7.333 8V5.333c0-.366.3-.666.667-.666.367 0 .667.3.667.666V8c0 .367-.3.667-.667.667zm.667 2.666H7.333V10h1.334v1.333z"
          />
        </svg>
        {error}
      </div>
    )
  }

  render() {
    const {
      proDropdownOptions,
      publisherDropdownOptions,
      serverError,
      invalidWriterOptions,
      saveAction = true,
      isFirstWriter,
    } = this.props
    const writerError = this.props.getValidationMessages('writer').toString()
    const publisherError = this.props
      .getValidationMessages('publisher')
      .toString()
    const proError = this.props.getValidationMessages('pro').toString()
    const splitError = this.props.getValidationMessages('split').toString()
    return (
      <div>
        <div className="track-writers__item track-writers__edit">
          <div className="track-writers__item__writer">
            <Label caption="Writer" required />
            <CustomDropdown
              value={this.state.writer}
              name="writer"
              placeholder="Select writer"
              className="form-custom-dropdown"
              invalidNewOptions={invalidWriterOptions}
              onChange={this._onWriterChange}
              allowCreate
              promptTextCreator={writerPromptTextCreator}
              async
              loadOptions={this.loadWriters}
            />
            {writerError && this.renderErrorMessage(writerError || serverError)}
          </div>
          <div className="track-writers__item__split">
            <TextField
              error={splitError ? splitError || serverError : null}
              label={
                <>
                  Split
                  <HelpTooltip>Enter writer’s song split here</HelpTooltip>{' '}
                </>
              }
              value={String(this.state.splitString || this.state.split || '')}
              name="split"
              placeholder="Enter %"
              onChange={this._onSplitChange}
              onBlur={this._onSplitBlur}
            />
          </div>
          <div className="track-writers__item__publisher">
            <Label caption="Publisher" />
            <CustomDropdown
              value={this.state.publisher}
              name="publisher"
              placeholder="Select publisher"
              className="form-custom-dropdown"
              options={publisherDropdownOptions}
              onChange={this._onPublisherChange}
              allowCreate
              promptTextCreator={publisherPromptTextCreator}
              truncateDisplayedOptions={100}
            />
            {publisherError &&
              this.renderErrorMessage(publisherError || serverError)}
          </div>
          <div className="track-writers__item__pro">
            <Label caption="PRO" />
            <CustomDropdown
              value={this.state.pro}
              name="pro"
              className="form-custom-dropdown"
              placeholder="Select PRO"
              options={proDropdownOptions}
              onChange={this._onProChange}
              allowCreate
              promptTextCreator={proPromptTextCreator}
              truncateDisplayedOptions={100}
            />
            {proError && this.renderErrorMessage(proError || serverError)}
          </div>
          <div className="track-writers__item__actions">
            <Label caption="&nbsp;" />
            <ButtonContainer>
              {saveAction && (
                <Button
                  disabled={!this.state.writer}
                  icon="check"
                  progressCaption=""
                  progressIndicator={this.state.updating}
                  onClick={this.onSubmit}
                />
              )}
              <Button
                disabled={isFirstWriter}
                icon="close"
                onClick={this.onCancel}
              />
            </ButtonContainer>
          </div>
        </div>
      </div>
    )
  }
}

export default connect(
  makeMapStateToProps,
  {},
)(validation(strategy)(EditWriter)) as any
